import React, { useState, useRef } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Table,
  Select,
  DatePicker,
  Button,
  Tabs,
  Space,
  Tag,
  Typography,
  Progress,
  Avatar,
  Divider,
  Radio,
  Tooltip,
  Badge,
  Rate,
  Empty,
} from 'antd';
import {
  UserOutlined,
  TeamOutlined,
  ShoppingOutlined,
  RiseOutlined,
  FallOutlined,
  BarChartOutlined,
  LineChartOutlined,
  PieChartOutlined,
  ReloadOutlined,
  CrownOutlined,
  DollarOutlined,
  FileTextOutlined,
  SyncOutlined,
  SearchOutlined,
  FilterOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import * as AntdCharts from '@ant-design/plots';

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { RangePicker } = DatePicker;

// 定义管家业绩数据接口
interface ButlerPerformance {
  id: string;
  name: string;
  avatar: string;
  department: string;
  position: string;
  level: string;
  memberCount: number;
  memberCountGrowth: number;
  orderCount: number;
  orderCountGrowth: number;
  revenue: number;
  revenueGrowth: number;
  repurchaseRate: number;
  repurchaseRateGrowth: number;
  conversion: number;
  averageOrderValue: number;
  satisfaction: number;
  memberRetention: number;
}

// 定义收益趋势数据接口
interface RevenueTrend {
  date: string;
  revenue: number;
  butler: string;
}

// 定义会员增长数据接口
interface MemberGrowth {
  date: string;
  count: number;
  type: string;
}

// 定义复购率数据接口
interface RepurchaseData {
  butler: string;
  rate: number;
}

// 模拟管家业绩数据
const mockButlerPerformance: ButlerPerformance[] = [
  {
    id: 'BT001',
    name: '李管家',
    avatar: 'https://xsgames.co/randomusers/avatar.php?g=male',
    department: '客户服务部',
    position: '高级管家',
    level: '高级',
    memberCount: 28,
    memberCountGrowth: 15,
    orderCount: 85,
    orderCountGrowth: 12,
    revenue: 127500,
    revenueGrowth: 18,
    repurchaseRate: 75,
    repurchaseRateGrowth: 8,
    conversion: 68,
    averageOrderValue: 1500,
    satisfaction: 4.8,
    memberRetention: 92,
  },
  {
    id: 'BT002',
    name: '王管家',
    avatar: 'https://xsgames.co/randomusers/avatar.php?g=male',
    department: '客户服务部',
    position: '资深管家',
    level: '资深',
    memberCount: 22,
    memberCountGrowth: 10,
    orderCount: 67,
    orderCountGrowth: 5,
    revenue: 96400,
    revenueGrowth: 9,
    repurchaseRate: 68,
    repurchaseRateGrowth: 4,
    conversion: 62,
    averageOrderValue: 1438,
    satisfaction: 4.6,
    memberRetention: 85,
  },
  {
    id: 'BT003',
    name: '张管家',
    avatar: 'https://xsgames.co/randomusers/avatar.php?g=female',
    department: '客户服务部',
    position: '资深管家',
    level: '资深',
    memberCount: 18,
    memberCountGrowth: 8,
    orderCount: 59,
    orderCountGrowth: 7,
    revenue: 82300,
    revenueGrowth: 12,
    repurchaseRate: 70,
    repurchaseRateGrowth: 6,
    conversion: 58,
    averageOrderValue: 1395,
    satisfaction: 4.5,
    memberRetention: 82,
  },
  {
    id: 'BT004',
    name: '刘管家',
    avatar: 'https://xsgames.co/randomusers/avatar.php?g=female',
    department: '客户服务部',
    position: '初级管家',
    level: '初级',
    memberCount: 12,
    memberCountGrowth: 12,
    orderCount: 38,
    orderCountGrowth: 38,
    revenue: 47600,
    revenueGrowth: 0,
    repurchaseRate: 55,
    repurchaseRateGrowth: 0,
    conversion: 48,
    averageOrderValue: 1253,
    satisfaction: 4.2,
    memberRetention: 76,
  },
  {
    id: 'BT005',
    name: '赵管家',
    avatar: 'https://xsgames.co/randomusers/avatar.php?g=male',
    department: '客户服务部',
    position: '初级管家',
    level: '初级',
    memberCount: 8,
    memberCountGrowth: 8,
    orderCount: 25,
    orderCountGrowth: 25,
    revenue: 31250,
    revenueGrowth: 0,
    repurchaseRate: 50,
    repurchaseRateGrowth: 0,
    conversion: 45,
    averageOrderValue: 1250,
    satisfaction: 4.1,
    memberRetention: 72,
  },
];

// 模拟收益趋势数据
const mockRevenueTrend: RevenueTrend[] = [
  { date: '2023-01', revenue: 8500, butler: '李管家' },
  { date: '2023-02', revenue: 9200, butler: '李管家' },
  { date: '2023-03', revenue: 10500, butler: '李管家' },
  { date: '2023-04', revenue: 11200, butler: '李管家' },
  { date: '2023-05', revenue: 12500, butler: '李管家' },
  { date: '2023-06', revenue: 13800, butler: '李管家' },
  { date: '2023-01', revenue: 6500, butler: '王管家' },
  { date: '2023-02', revenue: 7200, butler: '王管家' },
  { date: '2023-03', revenue: 8100, butler: '王管家' },
  { date: '2023-04', revenue: 8700, butler: '王管家' },
  { date: '2023-05', revenue: 9500, butler: '王管家' },
  { date: '2023-06', revenue: 10200, butler: '王管家' },
  { date: '2023-01', revenue: 5500, butler: '张管家' },
  { date: '2023-02', revenue: 6300, butler: '张管家' },
  { date: '2023-03', revenue: 7100, butler: '张管家' },
  { date: '2023-04', revenue: 7800, butler: '张管家' },
  { date: '2023-05', revenue: 8600, butler: '张管家' },
  { date: '2023-06', revenue: 9200, butler: '张管家' },
  { date: '2023-01', revenue: 0, butler: '刘管家' },
  { date: '2023-02', revenue: 0, butler: '刘管家' },
  { date: '2023-03', revenue: 0, butler: '刘管家' },
  { date: '2023-04', revenue: 3200, butler: '刘管家' },
  { date: '2023-05', revenue: 4100, butler: '刘管家' },
  { date: '2023-06', revenue: 4800, butler: '刘管家' },
  { date: '2023-01', revenue: 0, butler: '赵管家' },
  { date: '2023-02', revenue: 0, butler: '赵管家' },
  { date: '2023-03', revenue: 0, butler: '赵管家' },
  { date: '2023-04', revenue: 0, butler: '赵管家' },
  { date: '2023-05', revenue: 2800, butler: '赵管家' },
  { date: '2023-06', revenue: 3500, butler: '赵管家' },
];

// 模拟会员增长数据
const mockMemberGrowth: MemberGrowth[] = [
  { date: '2023-01', count: 35, type: '绑定管家会员' },
  { date: '2023-02', count: 42, type: '绑定管家会员' },
  { date: '2023-03', count: 48, type: '绑定管家会员' },
  { date: '2023-04', count: 58, type: '绑定管家会员' },
  { date: '2023-05', count: 72, type: '绑定管家会员' },
  { date: '2023-06', count: 88, type: '绑定管家会员' },
  { date: '2023-01', count: 120, type: '普通会员' },
  { date: '2023-02', count: 132, type: '普通会员' },
  { date: '2023-03', count: 145, type: '普通会员' },
  { date: '2023-04', count: 162, type: '普通会员' },
  { date: '2023-05', count: 178, type: '普通会员' },
  { date: '2023-06', count: 195, type: '普通会员' },
];

// 模拟复购率数据
const mockRepurchaseData: RepurchaseData[] = [
  { butler: '李管家', rate: 75 },
  { butler: '王管家', rate: 68 },
  { butler: '张管家', rate: 70 },
  { butler: '刘管家', rate: 55 },
  { butler: '赵管家', rate: 50 },
];

const ButlerPerformanceAnalysis: React.FC = () => {
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [activeTab, setActiveTab] = useState('performance');
  const [chartType, setChartType] = useState('revenue');
  const [butlerLevel, setButlerLevel] = useState('all');

  // 根据管家级别筛选数据
  const getFilteredButlerData = () => {
    if (butlerLevel === 'all') {
      return mockButlerPerformance;
    }
    return mockButlerPerformance.filter(butler => butler.level === butlerLevel);
  };

  // 表格列定义
  const columns: ColumnsType<ButlerPerformance> = [
    {
      title: '管家信息',
      key: 'info',
      fixed: 'left',
      width: 200,
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Avatar src={record.avatar} size="large" style={{ marginRight: 10 }} />
          <div>
            <Text strong>{record.name}</Text>
            <div>
              <Tag color={record.level === '高级' ? 'gold' : record.level === '资深' ? 'purple' : 'blue'}>
                {record.position}
              </Tag>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: '绑定会员数',
      key: 'memberCount',
      width: 150,
      sorter: (a, b) => a.memberCount - b.memberCount,
      render: (_, record) => (
        <div>
          <Text strong>{record.memberCount} 人</Text>
          <div>
            {record.memberCountGrowth > 0 ? (
              <Text type="success">
                <RiseOutlined /> +{record.memberCountGrowth}%
              </Text>
            ) : record.memberCountGrowth < 0 ? (
              <Text type="danger">
                <FallOutlined /> {record.memberCountGrowth}%
              </Text>
            ) : (
              <Text type="secondary">0%</Text>
            )}
          </div>
        </div>
      ),
    },
    {
      title: '产生订单数',
      key: 'orderCount',
      width: 150,
      sorter: (a, b) => a.orderCount - b.orderCount,
      render: (_, record) => (
        <div>
          <Text strong>{record.orderCount} 单</Text>
          <div>
            {record.orderCountGrowth > 0 ? (
              <Text type="success">
                <RiseOutlined /> +{record.orderCountGrowth}%
              </Text>
            ) : record.orderCountGrowth < 0 ? (
              <Text type="danger">
                <FallOutlined /> {record.orderCountGrowth}%
              </Text>
            ) : (
              <Text type="secondary">0%</Text>
            )}
          </div>
        </div>
      ),
    },
    {
      title: '创造收益',
      key: 'revenue',
      width: 150,
      sorter: (a, b) => a.revenue - b.revenue,
      render: (_, record) => (
        <div>
          <Text strong>¥{record.revenue.toLocaleString()}</Text>
          <div>
            {record.revenueGrowth > 0 ? (
              <Text type="success">
                <RiseOutlined /> +{record.revenueGrowth}%
              </Text>
            ) : record.revenueGrowth < 0 ? (
              <Text type="danger">
                <FallOutlined /> {record.revenueGrowth}%
              </Text>
            ) : (
              <Text type="secondary">0%</Text>
            )}
          </div>
        </div>
      ),
    },
    {
      title: '复购率',
      key: 'repurchaseRate',
      width: 150,
      sorter: (a, b) => a.repurchaseRate - b.repurchaseRate,
      render: (_, record) => (
        <div>
          <Progress
            percent={record.repurchaseRate}
            size="small"
            strokeColor={record.repurchaseRate >= 70 ? '#52c41a' : record.repurchaseRate >= 50 ? '#faad14' : '#ff4d4f'}
          />
          <div>
            {record.repurchaseRateGrowth > 0 ? (
              <Text type="success">
                <RiseOutlined /> +{record.repurchaseRateGrowth}%
              </Text>
            ) : record.repurchaseRateGrowth < 0 ? (
              <Text type="danger">
                <FallOutlined /> {record.repurchaseRateGrowth}%
              </Text>
            ) : (
              <Text type="secondary">0%</Text>
            )}
          </div>
        </div>
      ),
    },
    {
      title: '转化率',
      key: 'conversion',
      width: 150,
      sorter: (a, b) => a.conversion - b.conversion,
      render: (_, record) => (
        <Progress
          percent={record.conversion}
          size="small"
          strokeColor={record.conversion >= 60 ? '#52c41a' : record.conversion >= 45 ? '#faad14' : '#ff4d4f'}
        />
      ),
    },
    {
      title: '客单价',
      dataIndex: 'averageOrderValue',
      key: 'averageOrderValue',
      width: 120,
      sorter: (a, b) => a.averageOrderValue - b.averageOrderValue,
      render: (value) => `¥${value.toLocaleString()}`,
    },
    {
      title: '满意度',
      key: 'satisfaction',
      width: 120,
      sorter: (a, b) => a.satisfaction - b.satisfaction,
      render: (_, record) => (
        <div>
          <Rate disabled defaultValue={Math.round(record.satisfaction)} style={{ fontSize: 12 }} />
          <div>
            <Text>{record.satisfaction}</Text>
          </div>
        </div>
      ),
    },
    {
      title: '会员留存率',
      key: 'memberRetention',
      width: 150,
      sorter: (a, b) => a.memberRetention - b.memberRetention,
      render: (_, record) => (
        <Progress
          percent={record.memberRetention}
          size="small"
          strokeColor={record.memberRetention >= 85 ? '#52c41a' : record.memberRetention >= 70 ? '#faad14' : '#ff4d4f'}
        />
      ),
    },
  ];

  // 收益趋势图表配置
  const revenueConfig = {
    data: mockRevenueTrend,
    xField: 'date',
    yField: 'revenue',
    seriesField: 'butler',
    yAxis: {
      label: {
        formatter: (v: string | number) => `¥${v}`,
      },
    },
    legend: {
      position: 'top',
    },
    smooth: true,
  };

  // 会员增长图表配置
  const memberGrowthConfig = {
    data: mockMemberGrowth,
    xField: 'date',
    yField: 'count',
    seriesField: 'type',
    isGroup: true,
    columnStyle: {
      radius: [20, 20, 0, 0],
    },
    legend: {
      position: 'top',
    },
  };

  // 复购率图表配置
  const repurchaseConfig = {
    data: mockRepurchaseData,
    angleField: 'rate',
    colorField: 'butler',
    radius: 0.8,
    label: {
      type: 'outer',
      content: '{name} {percentage}',
    },
    interactions: [
      {
        type: 'pie-legend-active',
      },
    ],
  };

  // 获取总体统计数据
  const getTotalStats = () => {
    const filteredData = getFilteredButlerData();
    return {
      totalMembers: filteredData.reduce((sum, item) => sum + item.memberCount, 0),
      totalOrders: filteredData.reduce((sum, item) => sum + item.orderCount, 0),
      totalRevenue: filteredData.reduce((sum, item) => sum + item.revenue, 0),
      avgRepurchaseRate: filteredData.length > 0 
        ? Math.round(filteredData.reduce((sum, item) => sum + item.repurchaseRate, 0) / filteredData.length) 
        : 0,
    };
  };

  const stats = getTotalStats();

  // 修改图表渲染部分
  const renderChart = () => {
    if (chartType === 'revenue') {
      if (!mockRevenueTrend || mockRevenueTrend.length === 0) {
        return <Empty description="暂无收益趋势数据" />;
      }
      return <div style={{ height: 400 }}>{React.createElement(AntdCharts.Line, revenueConfig)}</div>;
    }
    
    if (chartType === 'members') {
      if (!mockMemberGrowth || mockMemberGrowth.length === 0) {
        return <Empty description="暂无会员增长数据" />;
      }
      return <div style={{ height: 400 }}>{React.createElement(AntdCharts.Column, memberGrowthConfig)}</div>;
    }
    
    if (chartType === 'repurchase') {
      if (!mockRepurchaseData || mockRepurchaseData.length === 0) {
        return <Empty description="暂无复购率数据" />;
      }
      return <div style={{ height: 400 }}>{React.createElement(AntdCharts.Pie, repurchaseConfig)}</div>;
    }
    
    return null;
  };

  return (
    <div className="butler-performance-analysis">
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <Title level={4} style={{ margin: 0 }}>管家业绩分析看板</Title>
          </Col>
          <Col flex="auto">
            <div style={{ float: 'right' }}>
              <Space>
                <Select
                  defaultValue="all"
                  style={{ width: 120 }}
                  onChange={(value) => setButlerLevel(value)}
                  options={[
                    { value: 'all', label: '全部级别' },
                    { value: '高级', label: '高级管家' },
                    { value: '资深', label: '资深管家' },
                    { value: '初级', label: '初级管家' },
                  ]}
                />
                <RangePicker onChange={(_, dateStrings) => setDateRange(dateStrings as [string, string] | null)} />
                <Button icon={<ReloadOutlined />}>刷新数据</Button>
                <Button type="primary" icon={<ExportOutlined />}>导出报告</Button>
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="绑定会员总数"
              value={stats.totalMembers}
              suffix="人"
              valueStyle={{ color: '#3f8600' }}
              prefix={<TeamOutlined />}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">较上月 <Text type="success">+12.5%</Text></Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="产生订单总数"
              value={stats.totalOrders}
              suffix="单"
              valueStyle={{ color: '#1890ff' }}
              prefix={<ShoppingOutlined />}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">较上月 <Text type="success">+8.3%</Text></Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="创造收益总额"
              value={stats.totalRevenue}
              prefix="¥"
              precision={2}
              valueStyle={{ color: '#cf1322' }}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">较上月 <Text type="success">+15.2%</Text></Text>
            </div>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="平均复购率"
              value={stats.avgRepurchaseRate}
              suffix="%"
              precision={1}
              valueStyle={{ color: '#722ed1' }}
              prefix={<RiseOutlined />}
            />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">较上月 <Text type="success">+5.8%</Text></Text>
            </div>
          </Card>
        </Col>
      </Row>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="管家业绩表现" key="performance">
          <Card>
            <Title level={5}>管家业绩排行榜</Title>
            <Table
              dataSource={getFilteredButlerData()}
              columns={columns}
              rowKey="id"
              scroll={{ x: 1500 }}
              pagination={{ pageSize: 10 }}
            />
          </Card>
        </TabPane>
        
        <TabPane tab="数据趋势分析" key="trends">
          <Card>
            <div style={{ marginBottom: 16 }}>
              <Radio.Group value={chartType} onChange={(e) => setChartType(e.target.value)} buttonStyle="solid">
                <Radio.Button value="revenue">收益趋势</Radio.Button>
                <Radio.Button value="members">会员增长</Radio.Button>
                <Radio.Button value="repurchase">复购率分析</Radio.Button>
              </Radio.Group>
            </div>
            
            {renderChart()}
          </Card>
        </TabPane>
        
        <TabPane tab="管家绩效详情" key="details">
          <Card>
            <Row gutter={16}>
              <Col span={8}>
                <Card title="顶级绩效管家" bordered={false}>
                  <div style={{ textAlign: 'center', padding: 16 }}>
                    <Badge count={<CrownOutlined style={{ color: '#ffd700' }} />}>
                      <Avatar size={80} src={mockButlerPerformance[0].avatar} />
                    </Badge>
                    <div style={{ marginTop: 16 }}>
                      <Title level={4}>{mockButlerPerformance[0].name}</Title>
                      <Tag color="gold">{mockButlerPerformance[0].position}</Tag>
                    </div>
                    <Divider />
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Statistic
                          title="绑定会员"
                          value={mockButlerPerformance[0].memberCount}
                          suffix="人"
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="产生订单"
                          value={mockButlerPerformance[0].orderCount}
                          suffix="单"
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="创造收益"
                          value={mockButlerPerformance[0].revenue}
                          prefix="¥"
                          precision={0}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="复购率"
                          value={mockButlerPerformance[0].repurchaseRate}
                          suffix="%"
                        />
                      </Col>
                    </Row>
                  </div>
                </Card>
              </Col>
              
              <Col span={16}>
                <Card title="绑定管家的高价值会员" bordered={false}>
                  <Table
                    dataSource={[
                      {
                        id: 'M10001',
                        name: '张三',
                        level: '黑钻卡',
                        totalSpent: 28500,
                        lastOrder: '2023-06-10',
                        butler: '李管家',
                        bindTime: '2023-03-20',
                      },
                      {
                        id: 'M10002',
                        name: '李四',
                        level: '铂金卡',
                        totalSpent: 16800,
                        lastOrder: '2023-05-25',
                        butler: '王管家',
                        bindTime: '2023-02-28',
                      },
                      {
                        id: 'M10006',
                        name: '孙七',
                        level: '黑钻卡',
                        totalSpent: 32400,
                        lastOrder: '2023-06-05',
                        butler: '李管家',
                        bindTime: '2023-01-15',
                      },
                    ]}
                    rowKey="id"
                    columns={[
                      {
                        title: '会员ID',
                        dataIndex: 'id',
                        key: 'id',
                      },
                      {
                        title: '会员姓名',
                        dataIndex: 'name',
                        key: 'name',
                      },
                      {
                        title: '会员等级',
                        dataIndex: 'level',
                        key: 'level',
                        render: (level) => (
                          <Tag color={level === '黑钻卡' ? 'black' : 'purple'}>{level}</Tag>
                        ),
                      },
                      {
                        title: '累计消费',
                        dataIndex: 'totalSpent',
                        key: 'totalSpent',
                        render: (value) => `¥${value.toLocaleString()}`,
                        sorter: (a, b) => a.totalSpent - b.totalSpent,
                      },
                      {
                        title: '最近消费',
                        dataIndex: 'lastOrder',
                        key: 'lastOrder',
                      },
                      {
                        title: '绑定管家',
                        dataIndex: 'butler',
                        key: 'butler',
                      },
                      {
                        title: '绑定时间',
                        dataIndex: 'bindTime',
                        key: 'bindTime',
                      },
                    ]}
                    pagination={false}
                  />
                </Card>
                
                <Card title="管家绑定带来的收益增长" style={{ marginTop: 16 }} bordered={false}>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Statistic
                        title="收益增长率"
                        value={35.8}
                        suffix="%"
                        precision={1}
                        valueStyle={{ color: '#3f8600' }}
                        prefix={<RiseOutlined />}
                      />
                      <Text type="secondary">绑定管家后的会员收益增长率</Text>
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="客单价提升"
                        value={28.6}
                        suffix="%"
                        precision={1}
                        valueStyle={{ color: '#3f8600' }}
                        prefix={<RiseOutlined />}
                      />
                      <Text type="secondary">绑定管家后的客单价提升</Text>
                    </Col>
                    <Col span={8}>
                      <Statistic
                        title="品类扩展率"
                        value={42.3}
                        suffix="%"
                        precision={1}
                        valueStyle={{ color: '#3f8600' }}
                        prefix={<RiseOutlined />}
                      />
                      <Text type="secondary">绑定管家后的消费品类扩展率</Text>
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>
          </Card>
        </TabPane>
      </Tabs>
    </div>
  );
};

export default ButlerPerformanceAnalysis; 